import { useContext } from "react";
import { useSelector } from "react-redux";
import Button from "@mui/material/Button";
import ListItem from "@mui/material/ListItem";
import SoftButton from "components/SoftButton";
import { COMMON_COMPONENT_TYPES } from "constants/flowDesigner";
import { ComponentPropsContext, ComponentPropsContextApi } from "layouts/Flow Designer/context";
import { getMcpOAuthStatus, startMcpOAuth } from "api/McpOAuthApi";
import { toast } from "react-toastify";
import { getEnv } from "helpers/environment";
import { useAuth0 } from "@auth0/auth0-react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import SoftTypography from "components/SoftTypography";

const ButtonListItem = ({ action, selectedNode, allowEditWorkflow }) => {
   const { actionForm, componentAction } = useContext(ComponentPropsContext);
   const { setActionForm, setComponentAction } = useContext(ComponentPropsContextApi);
   const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const { user } = useAuth0();
   const isFlowActive = currentSavedFlow?.shared_status === "active";
   const isMcpOAuthButton = selectedNode.key === COMMON_COMPONENT_TYPES.MCP_CLIENT && action.field === "authenticateMcpOAuth";
   const isMcpOAuthAuthenticated = isMcpOAuthButton && Boolean(actionForm.oauthConnectionId?.value);

   const executeVariableComponentButtonClick = () => {
      const index = componentAction.dataInsideAction.findIndex((item) => item.field === action.field);

      const generateValidVariableCount = () => {
         let validCount = 1;

         const variables = componentAction.dataInsideAction.filter(
            (action) => action.type === "string" && action.field.startsWith("var_")
         );

         const variablesNumber = variables.map((variable) => parseInt(variable.field.split("_")[1])).sort((a, b) => a - b);

         for (let num of variablesNumber) {
            if (num === validCount) {
               validCount++;
            } else if (num > validCount) {
               break;
            }
         }

         return validCount;
      };

      const validCount = generateValidVariableCount();

      if (componentAction.actionName === "Variables" && action.field === "addVariable") {
         setComponentAction({
               ...componentAction,
               dataInsideAction: [
               ...componentAction.dataInsideAction.slice(0, index),
               {
                  field: `var_${validCount}`,
                  label: "Name",
                  type: "string",
                  dataType: "string",
                                    placeholder: "",
                  value: "",
                   secondaryInput: {
                      field: `value_${validCount}`,
                      value: "",
                      label: "Value",
                      placeholder: "",
                      isRequired: false
                   },
                  defaultInput: {
                     field: `defVal_${validCount}`,
                     value: "",
                     label: "Default value",
                     placeholder: "",
                     isRequired: false
                  },
                  isRequired: true
               },
               ...(componentAction.dataInsideAction?.slice(index) ?? [])
            ]
         });
      }

      if (action.field === "addOutputReference") {
         const references = componentAction.dataInsideAction.filter((item) => item.field?.startsWith("reference_var_"));
         const nextIndex = Math.max(0, ...references.map((item) => Number(item.field.split("_").pop()) || 0)) + 1;
         setComponentAction({
            ...componentAction,
            dataInsideAction: [
               ...componentAction.dataInsideAction.slice(0, index),
               {
                  field: `reference_var_${nextIndex}`,
                  label: "",
                  type: "string",
                  placeholder: "",
                  value: "",
                  isRequired: true
               },
               ...(componentAction.dataInsideAction?.slice(index) ?? [])
            ]
         });
      }

   };

   const executeKeyValueAdditionClick = (e) => {
      const buttonName = e.target.name;
      const updateActionForm = (fieldName) => {
         if (actionForm[fieldName]?.value) {
            setActionForm({
               ...actionForm,
               [fieldName]: {
                  value: [...actionForm[fieldName].value, { key: "", value: "" }]
               }
            });
         }
      };

      switch (buttonName) {
         case "addParameter":
            updateActionForm("params");
            break;
         case "addHeader":
            updateActionForm("headers");
            break;
         case "addFormData":
            updateActionForm("formData");
            break;
      }
   };

   const executeRedisComponentButtonClick = () => {
      const index = componentAction.dataInsideAction.length - 2;

      const generateValidFieldCount = () => {
         let validCount = 1;

         const fields = componentAction.dataInsideAction.filter((action) => action.type === "string" && action.field.includes("field"));

         const fieldsNumber = fields.map((field) => parseInt(field.field.split("_")[1])).sort((a, b) => a - b);

         for (let num of fieldsNumber) {
            if (num === validCount) {
               validCount++;
            } else if (num > validCount) {
               break;
            }
         }

         return validCount;
      };

      const validCount = generateValidFieldCount();

      setComponentAction({
         ...componentAction,
         dataInsideAction: [
            ...componentAction.dataInsideAction.slice(0, index),
            {
               field: `field_${validCount}`,
               label: `Field ${validCount}`,
               type: "string",
               placeholder: "",
               value: "",
               secondaryInput: {
                  field: `Value_${validCount}`,
                  value: "",
                  label: `Default value ${validCount}`,
                  placeholder: "",
                  isRequired: true
               },
               isRequired: true
            },
            ...(componentAction.dataInsideAction?.slice(index) ?? [])
         ]
      });
   };

   const executeInvokeComponentButtonClick = () => {
      const parametersIndex = componentAction.dataInsideAction.findIndex((item) => item.field === "parameters");
      if (parametersIndex === -1) return;

      const parameters = {
         ...componentAction.dataInsideAction[parametersIndex]
      };

      const addParameterButtonIndex = parameters.listItems.findIndex((item) => item.field === "addParameter");

      const parameterIndexes = parameters.listItems
         .filter((item) => item.field.startsWith("var_"))
         .map((item) => Number(item.field.split("_").pop()) || 0);
      const nextIndex = Math.max(0, ...parameterIndexes) + 1;

      const newListItem = {
         field: `var_${nextIndex}`,
         label: "Name",
         type: "string",
         dataType: "string",
                  placeholder: "",
         value: "",
         secondaryInput: {
            field: `value_${nextIndex}`,
            value: "",
            label: "Value",
            placeholder: "",
            isRequired: false
         },
         defaultInput: {
            field: `defVal_${nextIndex}`,
            value: "",
            label: "Default value",
            placeholder: "",
            isRequired: false
         },
         isRequired: true
      };

      parameters.listItems = [...parameters.listItems.slice(0, addParameterButtonIndex), newListItem, ...parameters.listItems.slice(addParameterButtonIndex)];

      const newDataInsideAction = [...componentAction.dataInsideAction];
      newDataInsideAction[parametersIndex] = parameters;

      setComponentAction({
         ...componentAction,
         dataInsideAction: newDataInsideAction
      });
   };

   const executeMcpOAuthButtonClick = async () => {
      const serverUrl = actionForm.serverUrl?.value?.trim();

      if (!serverUrl) {
         toast.error("Enter the MCP server URL before authenticating.");
         return;
      }

      const popup = window.open("", "cxf-mcp-oauth", "popup,width=600,height=760");
      if (!popup) {
         toast.error("Allow popups to authenticate with this MCP server.");
         return;
      }

      popup.document.title = "Connecting MCP server";
      popup.document.body.innerHTML =
         '<p style="font-family:Arial,sans-serif;padding:24px">Connecting to the MCP authorization server...</p>';

      let oauthTimeout;
      let statusPoll;
      let authenticationComplete = false;

      const finishAuthentication = (connectionId) => {
         if (authenticationComplete) return;
         authenticationComplete = true;
         window.removeEventListener("message", onMessage);
         window.clearTimeout(oauthTimeout);
         window.clearInterval(statusPoll);
         if (!popup.closed) popup.close();

         setActionForm((currentForm) => ({
            ...currentForm,
            oauthConnectionId: { value: connectionId }
         }));
         toast.success("MCP server authenticated successfully.");
      };

      const onMessage = (event) => {
         const udasOrigin = new URL(getEnv("UDAS_API_SOCKET")).origin;
         if (event.origin !== udasOrigin || event.data?.type !== "cxf:mcp-oauth") return;

         if (event.data.error) {
            window.removeEventListener("message", onMessage);
            window.clearTimeout(oauthTimeout);
            window.clearInterval(statusPoll);
            toast.error(event.data.error);
            return;
         }

         finishAuthentication(event.data.connectionId);
      };

      window.addEventListener("message", onMessage);
      oauthTimeout = window.setTimeout(() => {
         window.removeEventListener("message", onMessage);
         window.clearInterval(statusPoll);
         if (!popup.closed) popup.close();
         toast.error("MCP authentication timed out. Please try again.");
      }, 10 * 60 * 1000);

      try {
         const response = await startMcpOAuth({
            tenantId: user?.stripeId,
            userId: user?.cxfUserId || user?.auth0_id || user?.id || user?.sub,
            serverUrl,
            frontendOrigin: window.location.origin,
            existingConnectionId: actionForm.oauthConnectionId?.value || null
         });
         popup.location.href = response.authorizationUrl;
         statusPoll = window.setInterval(async () => {
            try {
               const status = await getMcpOAuthStatus(user.stripeId, response.connectionId);
               if (status.status === "active") finishAuthentication(response.connectionId);
            } catch (error) {
		toast.error(error.message || "Unable to obtain MCP auth status");
            }
         }, 1000);
      } catch (error) {
         window.removeEventListener("message", onMessage);
         window.clearTimeout(oauthTimeout);
         window.clearInterval(statusPoll);
         popup.close();
         toast.error(error.message || "Unable to start MCP authentication.");
      }
   };

   const buttonClickHandler = (e) => {
      switch (selectedNode.key) {
         case COMMON_COMPONENT_TYPES.VARIABLES:
            executeVariableComponentButtonClick();
            break;
         case COMMON_COMPONENT_TYPES.REST_API:
         case COMMON_COMPONENT_TYPES.WEBHOOK:
         case COMMON_COMPONENT_TYPES.RESPOND:
            executeKeyValueAdditionClick(e);
            break;
         case COMMON_COMPONENT_TYPES.REDIS:
            executeRedisComponentButtonClick();
            break;
         case COMMON_COMPONENT_TYPES.INVOKE:
            executeInvokeComponentButtonClick();
            break;
         case COMMON_COMPONENT_TYPES.MCP_CLIENT:
            if (action.field === "authenticateMcpOAuth") executeMcpOAuthButtonClick();
            break;
      }
   };

   if (isMcpOAuthAuthenticated) {
      return (
         <ListItem
            key={action.label}
            sx={{
               display: "flex",
               flexDirection: "column",
               alignItems: "center",
            }}
         >
            <Box
               display="flex"
               alignItems="center"
               gap={1.5}
               width="100%"
               marginBottom={1.5}
            >
               <InfoOutlinedIcon
                  color="primary"
                  fontSize="small"
                  sx={{ mt: "1px", flexShrink: 0 }}
               />
               <Box minWidth={0}>
                  <SoftTypography variant="button" fontWeight="medium">
                     Authenticated
                  </SoftTypography>
                  <SoftTypography variant="caption" color="secondary" display="block">
                     This MCP server is connected and ready to configure.
                  </SoftTypography>
               </Box>
            </Box>
            <SoftButton
               component={Button}
               name={action.field}
               color="primary"
               variant="outlined"
               sx={{ width: "155px" }}
               onClick={buttonClickHandler}
               disabled={isFlowActive || !allowEditWorkflow}
            >
               Reauthenticate
            </SoftButton>
         </ListItem>
      );
   }

   return (
      <ListItem style={{ justifyContent: "center" }} key={action.label}>
         <SoftButton
            component={Button}
            name={action.field}
            color="primary"
            variant="outlined"
            sx={{ marginTop: "16px", width: "155px" }}
            onClick={buttonClickHandler}
            disabled={isFlowActive || !allowEditWorkflow}
         >
            {action.label}
         </SoftButton>
      </ListItem>
   );
};

export default ButtonListItem;
